import React, { useState } from "react";
import { getRarityLabel, getRarityColor } from "../data/cosmeticItems";
import { getPieceStyle } from "../data/shopItems";
import { purchaseCosmeticWithCrowns, purchaseShopItemWithCrowns, createCheckoutSession } from "../lib/api";
import CrownCoin from "./CrownCoin";
import { DS } from "../theme";

// ── Static board for preview rendering ────────────────────────────────────────

const PREVIEW_ROWS = [
  ["♜","♞","♝","♛","♚","♝","♞","♜"],
  ["♟","♟","♟","♟","♟","♟","♟","♟"],
  [null,null,null,null,null,null,null,null],
  [null,null,null,null,null,null,null,null],
  [null,null,null,null,null,null,null,null],
  [null,null,null,null,null,null,null,null],
  ["♙","♙","♙","♙","♙","♙","♙","♙"],
  ["♖","♘","♗","♕","♔","♗","♘","♖"],
];
const WHITE_SET = new Set(["♙","♖","♘","♗","♕","♔"]);

function MiniBoard({ item }) {
  const SQ = 34;

  function squareBg(r, c) {
    const isLight = (r + c) % 2 === 0;
    if (item.category === "board") {
      const light = item.preview?.lightSquare || item.light || "#F0D9B5";
      const dark  = item.preview?.darkSquare  || item.dark  || "#B58863";
      return isLight ? light : dark;
    }
    return isLight ? "#F0D9B5" : "#B58863";
  }

  function pieceStyle(piece) {
    if (!piece) return {};
    const isWhite = WHITE_SET.has(piece);
    if (item.category === "pieces") {
      return {
        color: isWhite ? item.preview.pieceColor : (item.preview.darkColor || "#1C1A2E"),
        textShadow: item.preview.glowColor
          ? `0 0 10px ${item.preview.glowColor}, 0 0 20px ${item.preview.glowColor}`
          : "0 1px 4px rgba(0,0,0,0.6)",
      };
    }
    if (item.category === "palette") return getPieceStyle("classic", item.id, isWhite);
    if (item.category === "design")  return getPieceStyle(item.id, "classic", isWhite);
    return { color: isWhite ? "#F0EEF8" : "#1C1A2E" };
  }

  const hasGrid = item.category === "board" &&
    item.preview?.gridColor && item.preview.gridColor !== "transparent";

  return (
    <div style={{
      display: "inline-grid",
      gridTemplateColumns: `repeat(8, ${SQ}px)`,
      gridTemplateRows:    `repeat(8, ${SQ}px)`,
      borderRadius: 8, overflow: "hidden",
      boxShadow: "0 8px 32px rgba(0,0,0,0.55)",
    }}>
      {PREVIEW_ROWS.map((row, r) => row.map((piece, c) => (
        <div
          key={`${r}${c}`}
          style={{
            width: SQ, height: SQ,
            background: squareBg(r, c),
            display: "flex", alignItems: "center", justifyContent: "center",
            ...(hasGrid ? { boxShadow: `inset 0 0 0 0.5px ${item.preview.gridColor}55` } : {}),
          }}
        >
          {piece && (
            <span style={{
              fontSize: SQ * 0.64,
              lineHeight: 1,
              userSelect: "none",
              pointerEvents: "none",
              ...pieceStyle(piece),
            }}>
              {piece}
            </span>
          )}
        </div>
      )))}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

export default function ShopPreviewModal({ item, isOwned, crowns, user, onClose, onPurchased }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(false);

  const isCrown  = item.priceType === "crowns";
  const canAfford = crowns >= item.price;

  async function handleBuy() {
    if (!user?.token || loading) return;
    setLoading(true);
    setError(null);
    try {
      if (isCrown) {
        const data = item.isStandardItem
          ? await purchaseShopItemWithCrowns(user.token, item.id, item.category)
          : await purchaseCosmeticWithCrowns(user.token, item.id);
        if (data.ok) {
          setSuccess(true);
          onPurchased(item.id, data.crowns);
          return;
        }
        if (data.error === 'insufficient_crowns')
          setError(`Not enough crowns — need ${(item.price - (data.needed ?? 0)).toLocaleString()} more 👑`);
        else if (data.error === 'already_owned')
          setError("You already own this.");
        else
          setError(data.error || "Purchase failed.");
      } else {
        const data = await createCheckoutSession(user.token, item.id, item.category);
        if (data.url) { window.location.href = data.url; return; }
        setError(data.error || "Could not start checkout.");
      }
    } catch {
      setError("Could not reach server. Try again.");
    }
    setLoading(false);
  }

  const rarityColor = item.rarity ? getRarityColor(item.rarity) : DS.accent;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(0,0,0,0.84)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, overflowY: "auto",
      }}
      onClick={onClose}
    >
      <div
        className="slide-up"
        style={{
          background: DS.surface,
          border: `1px solid ${rarityColor}28`,
          borderRadius: DS.radius + 4,
          padding: "24px 20px",
          maxWidth: 320, width: "100%",
          boxShadow: `0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px ${rarityColor}10`,
          display: "flex", flexDirection: "column", gap: 18,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Board preview */}
        <div style={{ display: "flex", justifyContent: "center", overflowX: "auto" }}>
          <MiniBoard item={item} />
        </div>

        {/* Name + rarity + desc */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
            <span className="cinzel" style={{ fontSize: 15, fontWeight: 700, color: DS.text }}>
              {item.name}
            </span>
            {item.rarity && (
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: "0.07em",
                textTransform: "uppercase", borderRadius: 4, padding: "2px 7px",
                color: rarityColor,
                border: `1px solid ${rarityColor}40`,
                background: `${rarityColor}10`,
              }}>
                {getRarityLabel(item.rarity)}
              </span>
            )}
          </div>
          {item.desc && (
            <div style={{ fontSize: 12, color: DS.textMuted, lineHeight: 1.55 }}>{item.desc}</div>
          )}
          {item.effects?.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
              {item.effects.map(e => (
                <span key={e} style={{
                  fontSize: 9, color: DS.textFaint,
                  background: DS.elevated, border: `1px solid ${DS.borderFaint}`,
                  borderRadius: 4, padding: "2px 6px",
                  textTransform: "uppercase", letterSpacing: "0.05em",
                }}>
                  {e.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Price + action */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {!success && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div className="cinzel" style={{ fontSize: 19, fontWeight: 700, color: isCrown ? "#D4AF37" : DS.gold }}>
                {isCrown ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <CrownCoin size={18} /> {item.price.toLocaleString()}
                  </span>
                ) : `$${(item.price / 100).toFixed(2)}`}
              </div>
              {isCrown && !isOwned && (
                <div style={{ fontSize: 11, color: canAfford ? DS.success : DS.danger }}>
                  {canAfford ? "✓ Affordable" : `Need ${(item.price - crowns).toLocaleString()} more 👑`}
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{ fontSize: 12, color: DS.danger }}>{error}</div>
          )}

          {success ? (
            <div style={{
              textAlign: "center", padding: "12px",
              background: `${DS.success}15`, border: `1px solid ${DS.success}35`,
              borderRadius: 8, fontSize: 13, color: DS.success,
            }}>
              ✓ Purchased! Equip in Armoury.
            </div>
          ) : isOwned ? (
            <div className="cinzel" style={{
              textAlign: "center", padding: "10px", borderRadius: 8,
              background: `${DS.accent}15`, border: `1px solid ${DS.accent}30`,
              fontSize: 12, color: DS.accent, fontWeight: 700,
            }}>
              ✓ Owned — equip in Armoury
            </div>
          ) : (
            <button
              className="btn btn-primary"
              style={{ width: "100%", justifyContent: "center", fontSize: 13 }}
              onClick={handleBuy}
              disabled={loading || !user || (isCrown && !canAfford)}
            >
              {loading ? "…" : isCrown
                ? `Buy  👑 ${item.price.toLocaleString()}`
                : `Buy  $${(item.price / 100).toFixed(2)}`}
            </button>
          )}

          <button
            className="btn btn-ghost"
            onClick={onClose}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {success ? "Done" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}
